import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DataVerification({ api }) {
  const [beaches, setBeaches] = useState([]);
  const [beachTotal, setBeachTotal] = useState(0);
  const [filterContinent, setFilterContinent] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterFirstLevel, setFilterFirstLevel] = useState("");
  const [filterSurface, setFilterSurface] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dbCountries, setDbCountries] = useState([]);
  const [dbFirstLevels, setDbFirstLevels] = useState([]);
  const [dbSurfaces, setDbSurfaces] = useState([]);
  const [page, setPage] = useState(0);
  const [selectedBeach, setSelectedBeach] = useState(null);

  const continents = ["North America", "South America", "Europe", "Asia", "Africa", "Oceania"];

  const fetchData = useCallback(async () => {
    try {
      const params = { limit: 50, offset: page * 50 };
      if (filterContinent) params.continent = filterContinent;
      if (filterCountry) params.country = filterCountry;
      if (filterFirstLevel) params.first_level = filterFirstLevel;
      if (filterSurface) params.surface = filterSurface;
      if (searchQuery) params.search = searchQuery;
      const data = await api.fetchBeaches(params);
      setBeaches(data.beaches || []);
      setBeachTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    }
  }, [api, page, filterContinent, filterCountry, filterFirstLevel, filterSurface, searchQuery]);

  const loadFilters = useCallback(async () => {
    try {
      const [countries, firstLevels, surfaces] = await Promise.all([
        api.fetchDbCountries(),
        api.fetchDbFirstLevels(filterCountry),
        api.fetchDbSurfaces(),
      ]);
      setDbCountries(countries);
      setDbFirstLevels(firstLevels);
      setDbSurfaces(surfaces);
    } catch (e) {
      console.error(e);
    }
  }, [api, filterCountry]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-5 gap-3">
        <Select value={filterContinent} onValueChange={(v) => { setFilterContinent(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger data-testid="filter-continent" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Continent" /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all">All Continents</SelectItem>
            {continents.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterCountry} onValueChange={(v) => { setFilterCountry(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger data-testid="filter-country" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
            <SelectItem value="all">All Countries</SelectItem>
            {dbCountries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterFirstLevel} onValueChange={(v) => { setFilterFirstLevel(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger data-testid="filter-first-level" className="bg-slate-800 border-slate-700"><SelectValue placeholder="State/Province" /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
            <SelectItem value="all">All States</SelectItem>
            {dbFirstLevels.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterSurface} onValueChange={(v) => { setFilterSurface(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger data-testid="filter-surface" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Surface" /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all">All Surfaces</SelectItem>
            {dbSurfaces.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>

        <Input
          data-testid="search-input"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          className="bg-slate-800 border-slate-700"
        />
      </div>

      {/* Results */}
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-sm border-cyan-500/50 text-cyan-400">{beachTotal.toLocaleString()} results</Badge>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="border-slate-700">Prev</Button>
          <span className="text-sm text-slate-400 self-center">Page {page + 1}</span>
          <Button variant="outline" size="sm" disabled={beaches.length < 50} onClick={() => setPage((p) => p + 1)} className="border-slate-700">Next</Button>
        </div>
      </div>

      <ScrollArea className="h-96">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Name</TableHead>
              <TableHead className="text-slate-400">Country</TableHead>
              <TableHead className="text-slate-400">State</TableHead>
              <TableHead className="text-slate-400">Surface</TableHead>
              <TableHead className="text-slate-400">Lat</TableHead>
              <TableHead className="text-slate-400">Lon</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {beaches.map((b) => (
              <TableRow
                key={`beach-${b.osm_id || b.beach_name}`}
                data-testid={`beach-row-${b.osm_id}`}
                className={`border-slate-800 cursor-pointer ${selectedBeach?.osm_id === b.osm_id ? "bg-cyan-500/10" : "hover:bg-slate-800/50"}`}
                onClick={() => setSelectedBeach(b)}
              >
                <TableCell className="font-medium">{b.beach_name}</TableCell>
                <TableCell>{b.country}</TableCell>
                <TableCell>{b.first_level || "-"}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{b.surface}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{b.lat?.toFixed(4)}</TableCell>
                <TableCell className="font-mono text-xs">{b.lon?.toFixed(4)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
